import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaBomb, FaFlag, FaSearch, FaFileAlt, FaTimes, FaPlay, FaShareAlt } from 'react-icons/fa'; // Добавлены иконки
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import { useArcadeShare } from '../../ui/useArcadeShare';
import './Minesweeper.css';

const Minesweeper = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);
  const [clickMode, setClickMode] = useState('open');
  const [finalData, setFinalData] = useState(null);

  // НОВЫЕ СОСТОЯНИЯ
  const [showReport, setShowReport] = useState(false);
  const [showExplosionStamp, setShowExplosionStamp] = useState(false);

  // ИНИЦИАЛИЗАЦИЯ МОДУЛЯ ШЕРИНГА
  const { boardRef, share, isSharing, isCapturing } = useArcadeShare(socket);

  const pointerDownIndexRef = useRef(null);

  useEffect(() => {
    if (!socket) return;
    // Запрашиваем состояние
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (data.type === 'update') {
        setState(data.state);
      } else if (data.type === 'finish') {
        setState(data.state); // Обновляем поле (вскрываем оставшееся)
        setFinalData(data);

        if (data.my?.isExploded) {
          // ПОРАЖЕНИЕ: Показываем только печать "ВЗРЫВ", отчет не открываем
          setShowExplosionStamp(true);
          window.croco?.haptic?.('error');
          setTimeout(() => setShowExplosionStamp(false), 2000);
        } else {
          // ПОБЕДА: Показываем рапорт автоматически сразу
          window.croco?.haptic?.('success');
          setTimeout(() => setShowReport(true), 1000);

        }
      } else if (data.type === 'game_over') {
        // Глобальное завершение по таймеру
        // ВАЖНО: Мы не обновляем state.completed, чтобы разрешить "песочницу"
        setFinalData(data);
        setShowReport(true);
        window.croco?.haptic?.('warning');
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, store]);

  const performAction = useCallback((type, index) => {
    if (!state || state.completed) return;

    if (type === 'flag') window.croco?.haptic?.('light');
    else if (type === 'chord') window.croco?.haptic?.('medium'); // Чуть сильнее для аккорда
    else window.croco?.haptic?.('selection');

    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type, index });
  }, [state, store.ref.work.methods]);

  // --- ИСПРАВЛЕННАЯ ОБРАБОТКА НАЖАТИЙ ---

  const handlePointerDown = (e, index) => {
    // Реагируем только на основную кнопку (левую/тач)
    if (e.button !== 0 && e.pointerType === 'mouse') return;

    pointerDownIndexRef.current = index;

    // ВАЖНО: Захватываем указатель. Это гарантирует, что событие up сработает на этом же элементе,
    // даже если палец сместится на пару пикселей за границу.
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = (e, index) => {
    // Если это не левая кнопка, игнорируем
    if (e.button !== 0 && e.pointerType === 'mouse') return;

    // Проверяем, что нажатие началось на этой же ячейке
    if (pointerDownIndexRef.current === index) {
        // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА:
        // Поскольку мы сделали setPointerCapture, событие сработает даже если мы утащили палец
        // на другой конец экрана. Нам нужно убедиться, что отпускание произошло ПОВЕРХ ячейки.
        const rect = e.currentTarget.getBoundingClientRect();
        const isInside = (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
        );

        if (isInside) {
            // Если мы всё еще над ячейкой (или очень близко к ней), считаем это кликом.
            // Это решает проблему "плохих краев", так как rect точен, в отличие от elementFromPoint.
            performAction(clickMode, index);
        }
    }

    pointerDownIndexRef.current = null;
  };

  const handleContextMenu = (e, index) => {
    e.preventDefault();
    pointerDownIndexRef.current = null;

    // ЛОГИКА ДЛЯ БЫСТРОГО ОТКРЫТИЯ (АККОРД)
    if (state?.revealed.includes(index)) {
        // Если ячейка открыта и это цифра, пробуем аккорд
        const val = state.grid[index];
        if (typeof val === 'number' && val > 0) {
            performAction('chord', index);
        }
        return;
    }

    // Если ячейка закрыта - ставим/снимаем флаг
    performAction('flag', index);
  };

  if (!state) return null;

  const renderCellContent = (val, idx) => {
    const isEnded = state.completed || state.isExploded;
    const isFlagged = state.flags.includes(idx);
    const isMine = val === 'M';

    // 1. ЛОГИКА ПОСЛЕ ОКОНЧАНИЯ ИГРЫ
    if (isEnded) {
        // Мина, на которой подорвались
        if (idx === state.explosionIndex) return <FaBomb className="ms-mine exploded" />;

        // Правильно помеченная мина
        if (isMine && isFlagged) return <FaBomb className="ms-mine won" />;

        // Ошибка: флаг там, где нет мины
        if (!isMine && isFlagged) return <FaTimes className="ms-flag-icon wrong" />;

        // Непомеченная мина
        if (isMine && !isFlagged) {
            // Если победа - красим в зеленый, если поражение - просто показываем
            return <FaBomb className={`ms-mine ${state.completed && !state.isExploded ? 'won' : 'missed'}`} />;
        }
    }

    // 2. ЛОГИКА ВО ВРЕМЯ ИГРЫ
    if (isFlagged && !isEnded) {
      return <FaFlag className="ms-flag-icon" />;
    }

    if (val === null || val === 0) return null;
    return <span className={`n-${val}`}>{val}</span>;
  };

  // --- ЭКРАН РЕПОРТА ---
  const renderReportModal = () => {
    if (!finalData || !showReport || isCapturing) return null;
    // Данные могут быть в `my` (game_over) или `finalData.my` (finish)
    const myStats = finalData.my || finalData.finalData?.my || {};
    const isWin = finalData.result === 'win'; // Проверяем результат
    // Если result нет (таймаут), считаем что не выиграл, но и не взорвался (песочница)
    const isExploded = myStats.isExploded;

    // Заголовок зависит от исхода
    let title = t('arcade.mines_report'); // "ОТЧЕТ"
    let statusText = t('arcade.mines_status_cleared'); // "БЕЗОПАСНО"
    let stampClass = 'ms-stamp-safe';

    if (isExploded) {
        statusText = t('arcade.mines_status_exploded');
        stampClass = 'ms-stamp-boom';
    } else if (!isWin) {
        // Таймаут
        title = t('arcade.game_over');
        statusText = t('arcade.time_up'); 
        stampClass = 'ms-stamp-boom'; // Или warning
    }

    return (
      <div className="ms-modal-overlay" onClick={() => setShowReport(false)}>
        <div className="cs-report-card" onClick={e => e.stopPropagation()} style={{ padding: '40px 30px' }}>
          <div className="cs-report-header" style={{ marginBottom: '35px' }}>
            <div className="cs-report-title" style={{ fontSize: '28px' }}>
              {title}
            </div>
          </div>

          <div className="cs-stats-grid">
            <div className="cs-stat-row" style={{ paddingBottom: '10px' }}>
               <span className="ms-report-stat-label">{t('arcade.wordduel_score')}</span>
               <span className="ms-report-stat-value good" style={{ fontSize: '32px', color: '#fff' }}>
                 {myStats.score || 0} ⭐️
               </span>
            </div>
            <div className="cs-stat-row" style={{ paddingBottom: '10px' }}>
               <span className="ms-report-stat-label">{t('arcade.wordduel_rank')}</span>
               <span className={`ms-report-stat-value ${myStats.rank === 'mines_rank_hero' ? 'hero' : ''}`} style={{ color: '#fff' }}>
                 {t(`arcade.${myStats.rank || 'mines_rank_rookie'}`)}
               </span>
            </div>
          </div>

          <div className={`ms-result-stamp ${stampClass}`}>
             {statusText}
          </div>

          <div style={{ margin: '15px 0' }}>
            <UnifiedLeaderboard results={finalData.leaderboard} myId={store.ref.telegram.telegramUserId} t={t} />
          </div>

             <div className="tr-btn-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'flex-end' }}>

                 <button
                     className="tr-btn tr-btn-primary"
                     style={{ background: '#3b82f6', color: '#fff', padding: '12px 0', fontSize: '15px' }}
                     onClick={() => share(myStats, { backgroundColor: '#1a1a1a' })}
                     disabled={isSharing}
                 >
                     <FaShareAlt style={{marginRight: '6px'}} />
                     {isSharing ? t('arcade.share_preparing') : t('arcade.share')}
                 </button>

                 <button
                     className="tr-btn tr-btn-primary"
                     style={{ padding: '12px 0', fontSize: '15px' }}
                     onClick={onClose}
                 >
                     {t('arcade.close')}
                 </button>

                 <button
                     className="tr-btn tr-btn-secondary"
                     style={{
                         padding: '12px 0',
                         fontSize: '15px',
                         background: 'transparent',
                         border: '2px solid #64748b',
                         color: '#f8fafc',
                         boxShadow: 'none'
                     }}
                     onClick={() => setShowReport(false)}
                 >
                     <FaPlay style={{marginRight: '6px'}} /> {t('arcade.stay')}
                 </button>

             </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ms-board" ref={boardRef}>
      {/* ПЕЧАТЬ ВЗРЫВА (ВРЕМЕННАЯ) */}
      {showExplosionStamp && (
        <div className="ms-explosion-stamp-overlay">
           <div className="ms-result-stamp ms-stamp-boom">
              {t('arcade.mines_status_exploded')}
           </div>
        </div>
      )}

      {renderReportModal()}

      <div className="ms-header">
        <div className="ms-stat">
           {t('arcade.wordduel_score')}: {state.score}
        </div>
        <div className="ms-stat danger">
          <FaBomb /> {state.minesTotal - state.flags.length}
        </div>
        <div className="ms-stat">
          {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </div>
      </div>

      <div className="ms-grid" style={{
        gridTemplateColumns: `repeat(${state.cols}, 1fr)`,
        aspectRatio: `${state.cols}/${state.rows}`
      }}>
        {state.grid.map((cell, idx) => {
          const isRevealed = state.revealed.includes(idx);
          // Блокируем взаимодействие, если локальная сессия окончена (completed),
          // НО не блокируем, если просто пришел game_over (таймаут), а completed = false
          const isInteractable = !state.completed;

          return (
            <div
              key={idx}
              data-index={idx}
              className={`ms-cell
                ${isRevealed ? 'revealed' : ''}
                ${state.flags.includes(idx) ? 'flagged' : ''}
                ${state.completed || state.isExploded ? 'game-over' : ''}
              `}
              onPointerDown={(e) => isInteractable && handlePointerDown(e, idx)}
              onPointerUp={(e) => isInteractable && handlePointerUp(e, idx)}
              onContextMenu={(e) => isInteractable && handleContextMenu(e, idx)}
              style={{
                fontSize: state.cols > 8 ? '18px' : '24px',
                touchAction: 'none'
              }}
            >
              {renderCellContent(cell, idx)}
            </div>
          );
        })}
      </div>

      <div className="ms-toggle-bar">
        {/* Показываем управление, если игра не закончена ИЛИ это песочница (время вышло, но не взорвался) */}
        {!state.completed ? (
          <>
            <button
              className={`ms-mode-btn ${clickMode === 'open' ? 'active' : ''}`}
              onClick={() => { setClickMode('open'); window.croco?.haptic?.('impactLight'); }}
            >
              <FaSearch /> {t('arcade.ms_mode_open')}
            </button>
            <button
              className={`ms-mode-btn ${clickMode === 'flag' ? 'active' : ''}`}
              onClick={() => { setClickMode('flag'); window.croco?.haptic?.('impactLight'); }}
            >
              <FaFlag /> {t('arcade.ms_mode_flag')}
            </button>
          </>
        ) : (
          <>
            {/* КНОПКИ ПОСЛЕ ПОЛНОГО ЗАВЕРШЕНИЯ */}
            <button
              className="ms-mode-btn active"
              onClick={() => { setShowReport(true); window.croco?.haptic?.('impactLight'); }}
            >
              <FaFileAlt /> {t('arcade.quiz_report')}
            </button>
            <button
              className="ms-mode-btn"
              style={{background: '#c0392b', color: '#fff', boxShadow: '0 4px 0 #7b1d1d'}}
              onClick={onClose}
            >
              <FaTimes /> {t('arcade.close')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Minesweeper;

